import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

const STATUS_OPTIONS = ["planned", "active", "stopped", "abandoned"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

// Use this for start_time, end_time, updated_at.
// No timezone conversion here.
// Backend already sends/stores the value you want to display.
function formatDBDateTime(value) {
  if (!value) return "-";

  const raw = String(value).trim();
  if (!raw) return "-";

  const clean = raw.replace("T", " ").split(".")[0];
  const [datePart, timePart = "00:00:00"] = clean.split(" ");

  const [yyyy, mm, dd] = datePart.split("-");
  const [hh = "00", mi = "00", ss = "00"] = timePart.split(":");

  if (!yyyy || !mm || !dd) return raw;

  let hour = parseInt(hh, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${dd}/${mm}/${yyyy} ${String(hour).padStart(2, "0")}:${mi}:${ss} ${ampm}`;
}

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

export default function EscortList() {
  const skin = useSkin();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const recordsPerPage = 10;

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route_list" : "/route_list"),
    [dashboardData.user_map]
  );

  const escortPage = useMemo(() => "/escort_master", []);
  const escortTrackingPage = useMemo(() => "/escort_live_tracking", []);

  const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  async function fetchPageData(local, token) {
    try {
      setLoading(true);

      const dashboardRes = await fetch(
        `${API_BASE_URL}/dashboard/admin/dashboard`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      const mergedDashboard = {
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      };

      setDashboardData(mergedDashboard);
      await fetchEscortRuns();
    } catch (error) {
      console.error("Escort list page error:", error);
      toast.error(error.message || "Failed to load escort list.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchEscortRuns() {
    try {
      setTableLoading(true);

      const res = await fetch(`${API_BASE_URL}/escort/runs`, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch escort runs");
      }

      const apiRows = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.data?.runs)
        ? data.data.runs
        : [];

      setAllRows(apiRows);
      setTotalRecords(apiRows.length);
    } catch (err) {
      console.error("Fetch escort runs error:", err);
      setAllRows([]);
      setTotalRecords(0);
      toast.error(err.message || "Something went wrong");
    } finally {
      setTableLoading(false);
    }
  }

  const filteredRows = useMemo(() => {
    const search = q.toLowerCase().trim();

    return allRows.filter((row) => {
      const matchesSearch =
        !search ||
        String(row.guard_name || "").toLowerCase().includes(search) ||
        String(row.title || "").toLowerCase().includes(search) ||
        String(row.from_name || "").toLowerCase().includes(search) ||
        String(row.to_name || "").toLowerCase().includes(search) ||
        String(row.member_id || "").toLowerCase().includes(search) ||
        String(row.vehicle || "").toLowerCase().includes(search);

      const matchesStatus =
        !status ||
        String(row.status || "").toLowerCase() === status.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [allRows, q, status]);

  const calculatedTotalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / recordsPerPage)
  );

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * recordsPerPage;
    const end = start + recordsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleClear = () => {
    setQ("");
    setStatus("");
    setPage(1);
    toast.success("Search cleared");
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > calculatedTotalPages) return;
    setPage(nextPage);
  };

  const handleDelete = async (runId) => {
    if (!window.confirm(
      `Delete escort run #${runId}? This will remove the run and all its points/events.`
    )) return;

    try {
      const res = await fetch(`${API_BASE_URL}/escort/runs/${runId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      toast.success(`Escort run #${runId} deleted successfully.`);
      await fetchEscortRuns();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const renderPagination = () => {
    if (calculatedTotalPages <= 1) return null;

    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(calculatedTotalPages, page + 2);

    if (page > 1) {
      items.push(
        <PageButton key="prev" onClick={() => handlePageChange(page - 1)}>
          ‹ Prev
        </PageButton>
      );
    }

    for (let i = start; i <= end; i += 1) {
      items.push(
        <PageButton
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    if (page < calculatedTotalPages) {
      items.push(
        <PageButton key="next" onClick={() => handlePageChange(page + 1)}>
          Next ›
        </PageButton>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-5 flex-wrap">
        {items}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading escort runs...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Escort Runs
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage all escort runs and their details
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Escort Route List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search, view, edit, track, and delete escort run records.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(escortPage)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                New Escort Run
              </button>
              <button
                type="button"
                onClick={() => navigate(routePage)}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Create Route
              </button>
              <button
                type="button"
                onClick={() => navigate("/route_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Route List
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Escorts
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {filteredRows.length}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Current Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {page} / {calculatedTotalPages}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Per Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {recordsPerPage}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Status
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {paginatedRows.length > 0 ? "Active" : "Empty"}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search guard, title, from, to..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className="flex-1 min-w-[260px] rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Search
            </button>

            {(q || status) && (
              <button
                type="button"
                onClick={handleClear}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Escort Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Escort Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {filteredRows.length} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            {tableLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
              </div>
            ) : paginatedRows.length > 0 ? (
              <>
                <table className="w-full min-w-[1200px] border-collapse">
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Run / Title</Th>
                      <Th>Guard</Th>
                      <Th>Vehicle</Th>
                      <Th>From → To</Th>
                      <Th>Status</Th>
                      <Th>Start Time</Th>
                      <Th>End Time</Th>
                      <Th>Points</Th>
                      <Th>Distance</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.map((row, index) => {
                      const runId = row.run_id;
                      const badge = String(row.status || "unknown").toLowerCase();
                      const serial = (page - 1) * recordsPerPage + index + 1;

                      return (
                        <tr key={runId || index} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                          <Td>
                            <span className="text-[var(--accent)] font-extrabold">
                              {serial}
                            </span>
                          </Td>

                          <Td>
                            <div className="font-extrabold text-[var(--text-primary)]">
                              #{valueOrDash(runId)}
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)] mt-0.5">
                              {valueOrDash(row.title)}
                            </div>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-2.5">
                              <div className="w-[38px] h-[38px] rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0 shadow-[0_3px_12px_var(--accent-glow)]">
                                {String(row.guard_name || "U").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-[13px] font-extrabold text-[var(--text-primary)]">
                                  {valueOrDash(row.guard_name)}
                                </div>
                                <div className="text-[11px] text-[var(--text-muted)] font-mono">
                                  {valueOrDash(row.member_id)}
                                </div>
                              </div>
                            </div>
                          </Td>

                          <Td>
                            <span className="font-mono text-[13px] text-[var(--text-primary)]">
                              {valueOrDash(row.vehicle)}
                            </span>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-1 text-[13px] text-[var(--text-primary)]">
                              <span className="text-[var(--accent)]">📍</span>
                              {valueOrDash(row.from_name)}
                            </div>
                            <div className="flex items-center gap-1 text-[13px] text-[var(--text-muted)] mt-0.5">
                              <span className="text-[#22c55e]">📍</span>
                              {valueOrDash(row.to_name)}
                            </div>
                          </Td>

                          <Td>
                            <StatusBadge status={badge} />
                          </Td>

                          <Td>
                            <span className="font-mono text-[12px] text-[var(--text-muted)]">
                              {formatDBDateTime(row.start_time)}
                            </span>
                          </Td>

                          <Td>
                            <span className="font-mono text-[12px] text-[var(--text-muted)]">
                              {formatDBDateTime(row.end_time)}
                            </span>
                          </Td>

                          <Td>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                              {Number(row.points_count || 0)}
                            </span>
                          </Td>

                          <Td>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)]">
                              {Number(row.distance_m || 0)}m
                            </span>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-2">
                              <IconButton
                                title="Track on Map"
                                className="bg-[#22c55e] hover:bg-[#16a34a]"
                                onClick={() =>
                                  navigate(
                                    `${escortTrackingPage}?member_id=${encodeURIComponent(
                                      row.member_id || ""
                                    )}&run_id=${runId}`
                                  )
                                }
                              >
                                <IconSvg type="map" />
                              </IconButton>

                              {!["active", "stopped"].includes(
                                String(row.status || "").toLowerCase()
                              ) && (
                                <>
                                  <IconButton
                                    title="Edit"
                                    className="bg-[var(--accent)] hover:bg-[var(--accent)]/80"
                                    onClick={() => navigate(`${escortPage}?edit_id=${runId}`)}
                                  >
                                    <IconSvg type="edit" />
                                  </IconButton>

                                  <IconButton
                                    title="Delete"
                                    className="bg-[var(--red)] hover:bg-[var(--red)]/80"
                                    onClick={() => handleDelete(runId)}
                                  >
                                    <IconSvg type="trash" />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {renderPagination()}
              </>
            ) : (
              <div className="py-12 px-5 text-center">
                <div className="text-5xl mb-3 text-[var(--text-faint)]">📋</div>
                <h3 className="text-[20px] font-extrabold text-[var(--text-primary)] m-0 mb-2">
                  No Escort Runs Found
                </h3>
                <p className="text-[13px] text-[var(--text-muted)] m-0">
                  {q || status ? "Try adjusting your search or filters." : "Create your first escort run."}
                </p>
                {!q && !status && (
                  <button
                    type="button"
                    onClick={() => navigate(escortPage)}
                    className="mt-4 border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                    style={{
                      background: "var(--btn-bg)",
                      color: "var(--btn-text)",
                      boxShadow: "0 4px 18px var(--accent-glow)",
                    }}
                  >
                    Create Escort Run
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Th({ children }) {
  return (
    <th
      className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)] whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] border-b border-[var(--divider)] align-middle"
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "active"
      ? "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
      : status === "planned"
      ? "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
      : status === "stopped"
      ? "bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.28)] text-[#f59e0b]"
      : status === "abandoned"
      ? "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
      : "bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-muted)]";

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-extrabold ${cls}`}>
      ● {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function IconSvg({ type }) {
  const commonProps = {
    className: "w-4 h-4",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2.2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (type === "edit") {
    return (
      <svg {...commonProps}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    );
  }

  if (type === "map") {
    return (
      <svg {...commonProps}>
        <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
        <path d="M9 3v15" />
        <path d="M15 6v15" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  );
}

function IconButton({ children, className = "", title = "", ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      {...props}
      className={`w-9 h-9 rounded-xl text-white inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-4 focus:ring-[var(--accent-faint)] ${className}`}
    >
      {children}
    </button>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-9 h-9 px-3 rounded-xl border text-[13px] font-extrabold transition-colors ${
        active
          ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
          : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
      }`}
    >
      {children}
    </button>
  );
}